<script setup lang="ts">
import ChefHatDiff from '@/assets/icons/Menus/ChefHatDiff.vue';
import BaseCard from '@/components/BaseComponents/BaseCard.vue';
import IngredientsList from '@/components/Menus/IngredientsList.vue';
import LoadingBar from '@/components/Skeletons/LoadingBar.vue';
import { useEventBus } from '@/composables/useEventBus';
import type { MessageSchema } from '@/i18n';
import { useDishesStore } from '@/stores/dishesStore';
import { type Dish } from '@/types/dish';
import { calculateDishPrice } from '@/utils/prices';
import Skeleton from 'primevue/skeleton';
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const eventBus = useEventBus();

const props = withDefaults(
  defineProps<{
    dish?: Dish;
    onboardingSummary?: boolean;
  }>(),
  {
    dish: undefined,
    onboardingSummary: false,
  },
);

const dishesStore = ref(useDishesStore());
const isLoading = computed(() => dishesStore.value.isGenerating || props.dish?.loading);

// Surveille quand le skeleton disparaît pour une carte d'onboarding
watch(
  () => isLoading.value,
  async (newIsLoading, oldIsLoading) => {
    console.log('[Onboarding] Watch isLoading:', {
      onboardingSummary: props.onboardingSummary,
      oldIsLoading,
      newIsLoading,
      hasIngredients: props.dish?.ingredients.length,
      dishId: props.dish?.id,
    });

    // Si c'est une carte d'onboarding et que le chargement vient de se terminer
    if (props.onboardingSummary && oldIsLoading && !newIsLoading && props.dish?.ingredients.length) {
      // Attendre que le DOM soit mis à jour
      await nextTick();
      console.log("[Onboarding] Détail de la recette chargé, émission de l'événement");
      eventBus.emit('onboarding:recipe-detail-loaded');
    }
  },
);
</script>

<template>
  <div class="generation-result-card" :class="{ 'onboarding-recipe-summary': onboardingSummary }">
    <BaseCard v-if="isLoading && !props.dish?.ingredients.length" :class="{ 'opacity-90': isLoading }" :onboarding-summary="onboardingSummary">
      <div class="mb-2 flex flex-row items-center gap-3">
        <h3 class="line-clamp-2 grow">{{ props.dish?.name }}</h3>
        <!-- <h3 class="line-clamp-2 grow">{{ isLoading ? `(${t('common.loading')})` : '' }} {{ props.dish?.name
          }}
        </h3> -->
        <!-- <Button rounded link :pt="{ root: { class: '!p-1.5 !w-8 !h-8' } }" :pt-options="{ mergeProps: true }"
          :disabled="isLoading">
          <template #icon>
            <ButtonRefresh />
          </template>
</Button> -->
      </div>
      <!-- <IngredientsList v-else :ingredients="props.dish.ingredients" gradient-from="from-primary-100"
        list-height="h-full" /> -->
      <Skeleton height=".75rem" width="85%" class="my-1 bg-primary-300 dark:bg-primary-500" />
      <Skeleton height=".75rem" width="85%" class="my-1 bg-primary-300 dark:bg-primary-500" />
      <Skeleton height=".75rem" width="85%" class="my-1 bg-primary-300 dark:bg-primary-500" />
      <Skeleton height=".75rem" width="85%" class="my-1 bg-primary-300 dark:bg-primary-500" />
      <div>
        <div class="flex flex-row items-center justify-between">
          <div class="flex h-6 w-24 flex-row gap-3 text-primary-950">
            <Skeleton height=".75rem" width="85%" class="bg-primary-300 dark:bg-primary-500" />
          </div>
        </div>
      </div>
      <LoadingBar v-if="isLoading && !props.dish?.ingredients.length" class="my-3" :duration-in-seconds="14" />
    </BaseCard>
    <BaseCard
      v-if="props.dish && props.dish.ingredients.length"
      :pt="{ root: { class: '!p-4' } }"
      :pt-options="{ mergeProps: true }"
      :loading-icon="'pi pi-spin pi-spinner'"
      :onboarding-summary="onboardingSummary"
    >
      <div class="mb-2 flex flex-row items-center gap-3">
        <h3 class="line-clamp-2 grow">{{ props.dish?.name }}</h3>
      </div>
      <div v-if="isLoading && !props.dish?.ingredients.length" class="flex flex-row">
        <p class="text-left">{{ t('menuDishView.loadingIngredientsLabel') }}</p>
      </div>
      <div v-else-if="props.dish" class="mb-3 mr-10">
        <IngredientsList :ingredients="props.dish.ingredients" gradient-from="from-primary-100" list-height="h-full" />
      </div>

      <div v-if="!isLoading && props.dish">
        <div class="flex flex-row items-center justify-between">
          <div class="flex h-6 w-24 flex-row gap-3 text-primary-950">
            <ChefHatDiff :level="props.dish.difficulty" />
            <!-- <ChefHatFull v-for="i in props.dish.difficulty" :key="i" />
            <ChefHatEmpty v-for="i in 4 - props.dish.difficulty" :key="i" /> -->
          </div>
          <div class="px-3 py-1 font-semibold font-titles text-xs text-primary-950">
            <div>~ {{ dish?.country?.currency_symbol }} {{ calculateDishPrice(props.dish) }} / pers.</div>
          </div>
        </div>
      </div>
    </BaseCard>
  </div>
</template>
